import React, { useEffect, useMemo, useState } from 'react';

import { Alert, Box, Button, CircularProgress, Snackbar } from '@mui/material';
import axios from 'axios';
import { FormikProvider, useFormik } from 'formik';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useDispatch } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { ConfirmDialog } from 'src/components/ConfimDialog';
import FullScreenLoader from 'src/components/FullscreenLoading';
import { useAuthContext } from 'src/context/AuthContextProvider';
import {
  getOperationFicChildren,
  getOperationsFicChildrenBatch,
  updateWalletWithFunds,
  updateWalletWithFundsForOwner,
} from 'src/services/wallet';
import { FOLLOWING_FIC_DASHBOARD_SUMMARY_QUERY_PREFIX } from 'src/services/wallet/queryKeys';
import { setBannerMessage } from 'src/store/display';
import { Operation } from 'src/types/operation';
import { Wallet } from 'src/types/wallet';
import * as Yup from 'yup';

import { VirtualizedFundTreeView } from './VirtualizedFundTreeView';
import {
  getExpandedNodeIdsForInvalidQuotas,
  toUpdateWalletFunds,
  UpdateWalletFundPayload,
} from './ficFundGroups';
import {
  discoverFicWalletRestriction,
  FundAdditionContext,
  getFicDiscoveryCandidates,
  resolveFicAdditionRestriction,
  sameOperationHashId,
} from './ficWalletRestriction';
import { DuplicateRecordsModal } from '../../CreateWallet/DuplicateRecordsModal';
import { useMonitorContext } from '../../MonitorContextProvider';
import { UpgradeModal } from '../../components/UpgradeModal';
import { FundSearchModal } from '../../components/shared/FundSearchModal';
import {
  getRootFundsForLimitCount,
  hasWalletFundIdentity,
  useFundsManagement,
} from '../../hooks/useFundsManagement';
import {
  MONITOR_LIMITS_QUERY_KEY,
  useMonitorLimits,
} from '../../hooks/useMonitorLimits';

type QuotaRecord = {
  id?: string;
  class: string;
  series: string | number;
  amount: number;
};

type FundData = {
  walletFundId?: number;
  operation: number;
  useAlertDefault: boolean;
  isFicParent: boolean;
  parentWalletFundId?: number;
  quotas: QuotaRecord[];
};

type FormValues = {
  walletId: string;
  funds: FundData[];
};

type UpdateWalletValues = {
  walletId: string;
  funds: UpdateWalletFundPayload[];
};

type EditWalletFundsProps = {
  wallet: Wallet;
  onSuccess: () => void;
};

const quotaValidationSchema = Yup.array().of(
  Yup.object().shape({
    class: Yup.string().required('Campo obrigatório'),
    series: Yup.string().required('Campo obrigatório'),
    amount: Yup.number()
      .required('Campo obrigatório')
      .min(1, 'Mínimo de 1 cota'),
  }),
);

const validationSchema = Yup.object().shape({
  funds: Yup.array().of(
    Yup.object().shape({
      operation: Yup.number().required('Campo obrigatório'),
      quotas: quotaValidationSchema.when('isFicParent', {
        is: true,
        then: Yup.array(),
        otherwise: quotaValidationSchema.min(
          1,
          'Pelo menos uma cota é obrigatória',
        ),
      }),
    }),
  ),
});

function getApiErrorMessage(error: unknown): string | undefined {
  if (!axios.isAxiosError(error)) return undefined;

  const responseData: unknown = error.response?.data;
  if (
    typeof responseData !== 'object' ||
    responseData === null ||
    !('error' in responseData)
  ) {
    return undefined;
  }

  const responseError = responseData.error;
  if (
    typeof responseError !== 'object' ||
    responseError === null ||
    !('message' in responseError) ||
    typeof responseError.message !== 'string'
  ) {
    return undefined;
  }

  return responseError.message;
}

export function EditWalletFunds({ wallet, onSuccess }: EditWalletFundsProps) {
  const [showAddModal, setShowAddModal] = useState(false);
  const [quotaErrors, setQuotaErrors] = useState<string[]>([]);
  const [expanded, setExpanded] = useState<string[]>(['root']);
  const [forceExpandErrors, setForceExpandErrors] = useState(false);
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const history = useHistory();
  const { auth } = useAuthContext();
  const { ownerId } = useParams<{ ownerId?: string }>();
  const { globalFundsCount, setGlobalFundsCount } = useMonitorContext();
  const { limits, getUniqueFundsCount } = useMonitorLimits();

  const isInvitedUser = !!ownerId;

  const [showDraftNoticeDialog, setShowDraftNoticeDialog] = useState(false);
  const [isAnyLoading, setIsAnyLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('Carregando...');
  const [isValidatingForm, setIsValidatingForm] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [attemptedFundsCount, setAttemptedFundsCount] = useState(0);
  const [fundAdditionContext, setFundAdditionContext] =
    useState<FundAdditionContext | null>(null);

  const ficDiscoveryCandidates = useMemo(
    () => getFicDiscoveryCandidates(wallet.walletFunds),
    [wallet.walletFunds],
  );
  const {
    data: ficWalletRestrictionDiscovery,
    isLoading: isLoadingFicWalletRestriction,
    isError: isFicWalletRestrictionError,
    refetch: retryFicWalletRestriction,
  } = useQuery(
    ['wallet-fic-children-batch', wallet.id, ficDiscoveryCandidates],
    () => discoverFicWalletRestriction(wallet, getOperationsFicChildrenBatch),
    {
      enabled: Boolean(auth?.isLoggedIn) && ficDiscoveryCandidates.length > 0,
      retry: false,
      refetchOnWindowFocus: false,
    },
  );

  const currentWalletFundsCount = useMemo(() => {
    const uniqueCNPJs = new Set(
      wallet.walletFunds.map((wf) => wf.operation.cnpj),
    );
    return uniqueCNPJs.size;
  }, [wallet.walletFunds]);

  const handleClearQuotaErrors = () => {
    setQuotaErrors([]);
  };

  const getLoadingMessage = () => {
    if (isValidatingForm) return 'Validando dados do formulário...';
    if (isUpdating) return 'Salvando alterações na carteira...';
    if (isValidating) return 'Validando arquivo CSV...';
    if (isLoadingCsvOperations) return 'Buscando dados dos fundos...';
    if (isLoadingOperations) return 'Carregando operações disponíveis...';
    if (isFetchingNextPage) return 'Buscando mais operações...';
    return 'Processando...';
  };

  const validateAllQuotas = () => {
    const errors: string[] = [];
    allFunds.forEach((fund) => {
      if (ficParentWalletFundIds.includes(fund.walletFundId ?? -1)) return;

      fund.quotas.forEach((quota) => {
        if (!quota.class || !quota.series || quota.amount <= 0) {
          errors.push(quota.id);
        }
      });
    });
    setQuotaErrors(errors);
  };

  const {
    allFunds,
    duplicateAlert,
    showDuplicateModal,
    duplicateGroups,
    document,
    csvValidation,
    isValidating,
    showValidationSummary,
    fundValidation,

    operations,
    isLoadingOperations,
    isLoadingCsvOperations,
    hasNextPage,
    isFetchingNextPage,
    searchValue,

    csvNotFoundCnpjs,
    csvNonConformingCnpjs,

    handleFundSelect,
    handleAddQuota,
    handleRemoveQuota,
    handleUpdateQuota,
    handleRemoveFund,
    handleSearchChange,
    handleConfirmDuplicates,
    handleCancelDuplicates,
    handleKeepAllDuplicates,
    handleConfirmImport,
    handleCancelImport,
    handleCloseDuplicateAlert,
    fetchNextPage,

    setDocument,
    setCsvValidation,
    setIsValidating,
  } = useFundsManagement({
    existingFunds: wallet.walletFunds,
    onSuccess,
    onClearQuotaErrors: handleClearQuotaErrors,
    onFundsChange: (updatedFunds) => {
      const formikFunds = updatedFunds.map((fund) => ({
        walletFundId: fund.walletFundId,
        operation: fund.id,
        useAlertDefault: fund.useAlertDefault,
        isFicParent:
          fund.compositionRole === 'fic_parent' ||
          ficParentWalletFundIds.includes(fund.walletFundId ?? -1),
        parentWalletFundId: fund.parentWalletFundId,
        quotas: fund.quotas.map((quota) => ({
          id: quota.id,
          class: quota.class,
          series: quota.series,
          amount: quota.amount,
        })),
      }));
      formik.setFieldValue('funds', formikFunds);
    },
    onShowUpgradeModal: (attemptedCount) => {
      setAttemptedFundsCount(attemptedCount);
      setShowUpgradeModal(true);
    },
    isInvitedUser,
  });

  const walletFundsLimitCount = useMemo(
    () => getUniqueFundsCount(getRootFundsForLimitCount(allFunds)),
    [allFunds, getUniqueFundsCount],
  );

  const ficParentWalletFundIds = useMemo(() => {
    const parentIds = new Set<number>();

    allFunds.forEach((fund) => {
      if (fund.compositionRole === 'fic_parent' && fund.walletFundId) {
        parentIds.add(fund.walletFundId);
      }

      if (fund.parentWalletFundId) {
        parentIds.add(fund.parentWalletFundId);
      }
    });

    return Array.from(parentIds);
  }, [allFunds]);

  const ficParentsFromBatch = ficWalletRestrictionDiscovery?.ficParents ?? [];

  const selectedFicParentFund = useMemo(() => {
    if (fundAdditionContext?.type !== 'fic-child') return undefined;

    return allFunds.find(
      (fund) => fund.walletFundId === fundAdditionContext.parentWalletFundId,
    );
  }, [allFunds, fundAdditionContext]);

  const selectedFicParentHashId = String(
    selectedFicParentFund?.hashId ?? '',
  ).trim();

  const batchResolvedSelectedParent = ficParentsFromBatch.some((parent) =>
    sameOperationHashId(parent.operation.hashId, selectedFicParentHashId),
  );

  const shouldFetchUnitFicChildren =
    fundAdditionContext?.type === 'fic-child' &&
    Boolean(selectedFicParentHashId) &&
    !isLoadingFicWalletRestriction &&
    !batchResolvedSelectedParent;

  const {
    data: fallbackFicParent,
    isLoading: isLoadingFallbackFicParent,
    isError: isFallbackFicParentError,
    refetch: retryFallbackFicParent,
  } = useQuery(
    ['operation-fic-children', selectedFicParentHashId],
    () => getOperationFicChildren(selectedFicParentHashId),
    {
      enabled: Boolean(auth?.isLoggedIn) && shouldFetchUnitFicChildren,
      retry: false,
      refetchOnWindowFocus: false,
    },
  );

  const ficParentsForRestriction = useMemo(() => {
    if (fallbackFicParent?.isFic && !batchResolvedSelectedParent) {
      return [...ficParentsFromBatch, fallbackFicParent];
    }

    return ficParentsFromBatch;
  }, [batchResolvedSelectedParent, fallbackFicParent, ficParentsFromBatch]);

  const ficAdditionRestriction = useMemo(
    () =>
      resolveFicAdditionRestriction({
        context: fundAdditionContext,
        funds: allFunds,
        ficParents: ficParentsForRestriction,
        isLoading:
          isLoadingFicWalletRestriction ||
          (shouldFetchUnitFicChildren && isLoadingFallbackFicParent),
        hasDiscoveryError: fallbackFicParent?.isFic
          ? false
          : shouldFetchUnitFicChildren
            ? isFallbackFicParentError || fallbackFicParent?.isFic === false
            : isFicWalletRestrictionError ||
              ficWalletRestrictionDiscovery?.hasDiscoveryError === true,
      }),
    [
      allFunds,
      fallbackFicParent,
      ficParentsForRestriction,
      ficWalletRestrictionDiscovery?.hasDiscoveryError,
      fundAdditionContext,
      isFallbackFicParentError,
      isFicWalletRestrictionError,
      isLoadingFallbackFicParent,
      isLoadingFicWalletRestriction,
      shouldFetchUnitFicChildren,
    ],
  );

  const { mutate: updateWalletMutation, isLoading: isUpdating } = useMutation({
    mutationFn: (values: UpdateWalletValues) => {
      if (isInvitedUser && ownerId) {
        return updateWalletWithFundsForOwner(values.walletId, values, ownerId);
      }
      return updateWalletWithFunds(values.walletId, values);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['wallet-warning-summary', wallet.id]);
      queryClient.invalidateQueries(
        FOLLOWING_FIC_DASHBOARD_SUMMARY_QUERY_PREFIX,
      );
      queryClient.invalidateQueries(MONITOR_LIMITS_QUERY_KEY);

      const newGlobalCount =
        globalFundsCount -
        currentWalletFundsCount +
        getUniqueFundsCount(allFunds);
      setGlobalFundsCount(newGlobalCount);

      if (isInvitedUser) {
        setShowDraftNoticeDialog(true);
      } else {
        dispatch(
          setBannerMessage({
            message: 'Carteira atualizada com sucesso!',
            type: 'success',
            autoclose: true,
          }),
        );
        onSuccess();
      }
    },
    onError: (error: unknown) => {
      const errorMessage = getApiErrorMessage(error);

      dispatch(
        setBannerMessage({
          message: `Não foi possível atualizar a carteira. ${errorMessage || 'Erro desconhecido'}`,
          type: 'error',
          autoclose: true,
        }),
      );
    },
  });

  const initialValues: FormValues = useMemo(() => {
    const funds = allFunds.map((fund) => ({
      walletFundId: fund.walletFundId,
      operation: fund.id,
      useAlertDefault: fund.useAlertDefault,
      isFicParent:
        fund.compositionRole === 'fic_parent' ||
        ficParentWalletFundIds.includes(fund.walletFundId ?? -1),
      parentWalletFundId: fund.parentWalletFundId,
      quotas: fund.quotas.map((quota) => ({
        id: quota.id,
        class: quota.class,
        series: quota.series,
        amount: quota.amount,
      })),
    }));

    return {
      walletId: wallet.id.toString(),
      funds,
    };
  }, [allFunds, ficParentWalletFundIds, wallet.id]);

  const formik = useFormik<FormValues>({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    onSubmit: (values) => {
      const formattedValues = {
        ...values,
        funds: toUpdateWalletFunds(
          allFunds.map((fund) => {
            const formFund = values.funds.find((formFund) =>
              hasWalletFundIdentity(
                fund,
                formFund.operation,
                formFund.parentWalletFundId,
              ),
            );

            return {
              ...fund,
              useAlertDefault:
                formFund?.useAlertDefault ?? fund.useAlertDefault,
              parentWalletFundId:
                formFund?.parentWalletFundId ?? fund.parentWalletFundId,
            };
          }),
          ficParentWalletFundIds,
        ),
      };
      updateWalletMutation(formattedValues);
    },
  });

  const handleCloseDraftNoticeDialog = () => {
    setShowDraftNoticeDialog(false);
    history.push(`/monitor/${ownerId}?tab=pending`);
  };

  const handleOpenAddModal = (context: FundAdditionContext) => {
    setFundAdditionContext(context);
    setLoadingMessage('Abrindo modal de busca...');
    setIsAnyLoading(true);

    setTimeout(() => {
      setShowAddModal(true);
      setIsAnyLoading(false);
    }, 200);
  };

  const handleOpenGlobalAddModal = () => {
    handleOpenAddModal({ type: 'global' });
  };

  const handleOpenFicChildAddModal = (parentWalletFundId: number) => {
    handleOpenAddModal({ type: 'fic-child', parentWalletFundId });
  };

  const handleCloseAddModal = () => {
    setFundAdditionContext(null);
    setLoadingMessage('Fechando modal...');
    setIsAnyLoading(true);

    setTimeout(() => {
      setShowAddModal(false);
      setIsAnyLoading(false);
    }, 200);
  };

  const handleFundSelectAndClose = (fund: Operation) => {
    setLoadingMessage('Adicionando fundo à carteira...');
    setIsAnyLoading(true);

    handleFundSelect(
      fund,
      fundAdditionContext?.type === 'fic-child'
        ? fundAdditionContext.parentWalletFundId
        : undefined,
    );

    setTimeout(() => {
      setShowAddModal(false);
      setIsAnyLoading(false);
    }, 300);
  };

  const handleConfirmImportAndClose = () => {
    setLoadingMessage('Processando importação...');
    setIsAnyLoading(true);

    handleConfirmImport();

    setTimeout(() => {
      setShowAddModal(false);
      setIsAnyLoading(false);
    }, 500);
  };

  useEffect(() => {
    if (allFunds.length > 0) {
      validateAllQuotas();
    }
  }, [allFunds]);

  useEffect(() => {
    if (!forceExpandErrors) return;
    setForceExpandErrors(false);
  }, [forceExpandErrors]);

  useEffect(() => {
    const loading =
      isLoadingOperations ||
      isLoadingCsvOperations ||
      isUpdating ||
      isValidating ||
      isValidatingForm ||
      isFetchingNextPage;

    setIsAnyLoading(loading);

    if (loading) {
      setLoadingMessage(getLoadingMessage());
    }
  }, [
    isLoadingOperations,
    isLoadingCsvOperations,
    isUpdating,
    isValidating,
    isValidatingForm,
    isFetchingNextPage,
  ]);

  return (
    <FormikProvider value={formik}>
      <FullScreenLoader open={isAnyLoading} message={loadingMessage} />
      <Box sx={{ width: '100%' }}>
        {limits.hasLimits && !isInvitedUser && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {limits.maxFunds === null
              ? 'Fundos ilimitados por carteira.'
              : `Limite de fundos: ${walletFundsLimitCount}/${limits.maxFunds} fundos usados nesta carteira`}
          </Alert>
        )}

        <ConfirmDialog
          open={showDraftNoticeDialog}
          onClose={handleCloseDraftNoticeDialog}
          submitAction={handleCloseDraftNoticeDialog}
          hasActionButton={false}
          title="Carteira enviada para revisão"
          subTitle="Suas alterações foram salvas. A carteira foi movida para o estado de rascunho e ela precisa ser enviada para aprovação novamente."
        />

        <UpgradeModal
          open={showUpgradeModal}
          onClose={() => setShowUpgradeModal(false)}
          type="fund"
          maxLimit={limits.maxFunds}
          currentCount={walletFundsLimitCount}
          attemptedCount={attemptedFundsCount}
          userName={auth?.user?.name}
          userId={auth?.user?.id}
        />

        <DuplicateRecordsModal
          open={showDuplicateModal}
          duplicateGroups={duplicateGroups}
          onConfirm={handleConfirmDuplicates}
          onCancel={handleCancelDuplicates}
          onKeepAll={handleKeepAllDuplicates}
        />

        <FundSearchModal
          open={showAddModal}
          onClose={handleCloseAddModal}
          onFundSelect={handleFundSelectAndClose}
          onConfirmImport={handleConfirmImportAndClose}
          onCancelImport={handleCancelImport}
          operations={operations}
          isLoadingOperations={isLoadingOperations}
          searchValue={searchValue}
          onSearchChange={handleSearchChange}
          onLoadMore={fetchNextPage}
          hasNextPage={!!hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          document={document}
          setDocument={setDocument}
          csvValidation={csvValidation}
          setCsvValidation={setCsvValidation}
          isValidating={isValidating}
          setIsValidating={setIsValidating}
          showValidationSummary={showValidationSummary}
          fundValidation={fundValidation}
          csvNotFoundCnpjs={csvNotFoundCnpjs}
          csvNonConformingCnpjs={csvNonConformingCnpjs}
          restrictedOperations={ficAdditionRestriction.restrictedOperations}
          isRestrictionLoading={ficAdditionRestriction.isLoading}
          isRestrictionError={ficAdditionRestriction.isError}
          onRetryRestriction={
            fundAdditionContext?.type === 'fic-child'
              ? () => {
                  retryFicWalletRestriction();
                  if (shouldFetchUnitFicChildren) {
                    retryFallbackFicParent();
                  }
                }
              : undefined
          }
          isSelectionBlocked={ficAdditionRestriction.isSelectionBlocked}
          restrictionMessage={ficAdditionRestriction.restrictionMessage}
        />

        <VirtualizedFundTreeView
          walletFunds={allFunds}
          onAddFund={handleOpenGlobalAddModal}
          onUpdateQuota={handleUpdateQuota}
          onAddQuota={handleAddQuota}
          onRemoveQuota={handleRemoveQuota}
          onRemoveFund={handleRemoveFund}
          quotaErrors={quotaErrors}
          expanded={expanded}
          onExpandedChange={setExpanded}
          forceExpandErrors={forceExpandErrors}
          onAddFicChild={handleOpenFicChildAddModal}
        />

        <Box
          sx={{ mt: 4, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            color="primary"
            disabled={isUpdating || isValidatingForm || allFunds.length === 0}
            onClick={async () => {
              setIsValidatingForm(true);

              try {
                const errors = await formik.validateForm();

                if (Object.keys(errors).length > 0 && errors.funds) {
                  const invalidQuotaNodeIds =
                    getExpandedNodeIdsForInvalidQuotas(allFunds);

                  setExpanded((prev) => [
                    ...new Set([...prev, ...invalidQuotaNodeIds]),
                  ]);
                  setForceExpandErrors(true);
                  dispatch(
                    setBannerMessage({
                      message:
                        'Preencha os campos obrigatórios de cota antes de salvar as alterações.',
                      type: 'error',
                      autoclose: true,
                    }),
                  );
                  return;
                }

                formik.handleSubmit();
              } finally {
                setIsValidatingForm(false);
              }
            }}>
            {isUpdating || isValidatingForm ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              'Salvar Alterações'
            )}
          </Button>
        </Box>

        <Snackbar
          open={duplicateAlert.open}
          autoHideDuration={6000}
          onClose={handleCloseDuplicateAlert}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
          <Alert
            onClose={handleCloseDuplicateAlert}
            severity="warning"
            sx={{ width: '100%' }}>
            {duplicateAlert.message}
          </Alert>
        </Snackbar>
      </Box>
    </FormikProvider>
  );
}
